/* global Turbo */
import { Controller } from '@hotwired/stimulus'

const eventName = 'confirm'
const eventNameRegexp = new RegExp(`${eventName}->`)

/**
 * Handle Turbo confirm with custom confirmation dialog
 *
 * Our confirmation dialog use a modal <dialog> similar to our other modals. Content of the confirmation dialog can
 * be customized through data attributes for title, content, button labels and style.
 *
 * @see TurboHelper#confirm_data_attributes for the recommended way to set Turbo confirm on a form or a link.
 *
 * Turbo handles confirmation in two different ways:
 *
 * 1. If the element with `data-turbo-confirm` is a form
 *
 * `submit` events on the form are caught and the confirmation method is called. It receives:
 *
 *   * the value of `data-turbo-confirm` as first argument (so `window.confirm` will work)
 *   * the form as second argument
 *   * the button clicked to trigger form submission as third argument.
 *
 * The confirmation method must return a Promise that resolve to a boolean, indicating whether or not form submission
 * should go on.
 *
 * 2. If the element with `data-turbo-confirm` is a link with `data-turbo-method`
 *
 * On a `click` event, Turbo will build a fake form to submit. That form will get copies of Turbo related
 * data-attributes (`data-turbo-confirm`, `data-turbo-frame`, `data-turbo-action`, `data-turbo-stream`), its action
 * will be set to link's `href` and its method to the value of `data-turbo-method`. This fake form will be submitted to
 * fallback on the "normal" Turbo confirm flow described above.
 *
 * The Turbo confirm method will receive the fake form as second argument and nothing as third argument.
 *
 * @see https://github.com/hotwired/turbo/issues/811 for the original issue
 * @see https://github.com/hotwired/turbo/pull/856 for the (pending) fix
 *
 * Due to this inconsistency in the way Turbo handles confirmation between forms and links, we need to find a way to
 * always get a reference to the element with `data-turbo-confirm`, as it also carry configuration options for our
 * confirmation dialog. It's also on this element we will dispatch a custom `confirm` event so Stimulus actions can be
 * hooked after confirmation (with `data-action="confirm->controller#action"`).
 *
 * To do so, we use the default `data-turbo-confirm` attribute NOT to store the message that will be used (as it is
 * by default when using browser's `.confirm` method) but to store a unique identifier we can use to find our
 * configurator element back.
 *
 * ---
 *
 * Beware of Turbo confirm known issues!
 *
 * To use Turbo confirm on a form where the submit button is outside the form (hence use the `form` attribute), the two
 * must have a common ancestor with `data-turbo="true"`.
 * @see https://github.com/hotwired/turbo/issues/1246
 */
export default class extends Controller {
  static targets = ['body', 'title', 'cancelButton', 'defaultButton', 'warningButton']

  initialize() {
    Turbo.config.forms.confirm = this.#confirm.bind(this)
  }

  closeDialog() {
    this.element.close('cancel')
  }

  #configure(configurator) {
    // Allows last minute customisations of the confirmation message
    configurator.dispatchEvent(new Event(`${eventName}.configure`))
    const config = configurator.dataset

    this.#updateContent({
      body: config.turboConfirmBody,
      buttonLabel: config.turboConfirmButtonLabel,
      buttonState: config.turboConfirmButtonState,
      cancelLabel: config.turboConfirmCancelLabel,
      title: config.turboConfirmTitle
    })
  }

  /**
   * @param {String}                uuid    Unique identifier to find the original element with `data-turbo-confirm`
   * @param {HTMLFormElement}       form    Form to be submitted (Potentially a fake form build by Turbo)
   * @param {HTMLElement|undefined} button  Button clicked to submit the form
   * @returns {Promise<boolean>}
   */
  #confirm(uuid, form, button) {
    const dialog = this.element
    const submitter = this.#findSubmitter(uuid, button)
    const configurator = this.#findConfigurator(form, submitter)

    this.#configure(configurator)

    return new Promise(resolve => {
      dialog.addEventListener(
        'close',
        () => {
          const result = dialog.returnValue === 'confirm'

          if (result && this.#shouldDispatchConfirmEvent(submitter)) {
            // Allows Stimulus actions to be hooked after confirmation.
            submitter.dispatchEvent(new Event(eventName, { bubbles: true }))
            return resolve(false)
          }

          return resolve(result)
        },
        { once: true }
      )

      dialog.showModal()
    })
  }

  /**
   * Uses `closest` to find the nearest submitter ancestor with `data-turbo-confirm` (maybe the submitter itself,
   * e.g. when it's a link). If submitter is a button outside the form (as in our lightboxes), no ancestor will be
   * found so we need to fallback on the form.
   */
  #findConfigurator(form, submitter) {
    const configurator = submitter.closest('[data-turbo-confirm]')
    if (configurator) {
      return configurator
    }
    return form
  }

  #findSubmitter(uuid, submitter) {
    if (submitter) {
      return submitter
    }

    // `:not(form)` so we won't get the fake form Turbo built.
    const originalSubmitter = document.querySelector(`[data-turbo-confirm="${uuid}"]:not(form)`)
    if (!originalSubmitter) {
      throw new Error('Use TurboHelper#confirm_data_attributes to setup Turbo confirm')
    }

    return originalSubmitter
  }

  #shouldDispatchConfirmEvent(submitter) {
    return eventNameRegexp.test(submitter.dataset.action)
  }

  #updateContent({ body, buttonLabel, buttonState, cancelLabel, title }) {
    this.bodyTarget.textContent = body
    this.titleTarget.textContent = title
    this.cancelButtonTarget.textContent = cancelLabel
    this.defaultButtonTarget.textContent = buttonLabel
    this.warningButtonTarget.textContent = buttonLabel

    this.defaultButtonTarget.classList.add('hidden')
    this.warningButtonTarget.classList.add('hidden')
    if (buttonState === 'warning') {
      this.warningButtonTarget.classList.remove('hidden')
    } else {
      this.defaultButtonTarget.classList.remove('hidden')
    }
  }
}
